import { useCallback, useEffect, useRef, useState } from 'react';

import useAccountSearch from '../../shared/useAccountSearch';
import {
  fetchAdminAccounts,
  fetchFormOptions,
} from '../services/createUserService';

const EMPTY_OPTIONS = {
  jobRoles: [],
  countries: [],
  states: {},
  phoneCountryCodes: [],
  permissions: [],
};

/**
 * Option lists for the Create User wizard (PD-4064).
 *
 * Everything but accounts is bounded, so it loads once when the wizard first
 * opens. Accounts are searched and paged server side, which useAccountSearch
 * owns — extracted so the Add To Account modal can drive the same paging
 * against a different endpoint.
 *
 * @param {boolean} isOpen fetches on the first open, not on mount, so closing
 *   and reopening the wizard does not refetch.
 */
const useFormOptions = (isOpen) => {
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const hasLoaded = useRef(false);

  const {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    searchAccounts,
  } = useAccountSearch(fetchAdminAccounts, isOpen);

  const loadOptions = useCallback(() => fetchFormOptions(), []);

  useEffect(() => {
    if (!isOpen || hasLoaded.current) return undefined;

    hasLoaded.current = true;
    let cancelled = false;

    loadOptions()
      .then((formOptions) => {
        if (cancelled) return;
        setOptions({ ...EMPTY_OPTIONS, ...formOptions });
      })
      // A failed option list leaves the selects empty rather than breaking the
      // wizard, matching how useFilterOptions treats the same failure. Allow a
      // retry on the next open.
      .catch(() => {
        hasLoaded.current = false;
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, loadOptions]);

  return {
    accountOptions,
    accountsTotal,
    hasMoreAccounts,
    isLoadingAccounts,
    loadMoreAccounts,
    options,
    searchAccounts,
  };
};

export default useFormOptions;
